import type {
  AgentTaskListState,
  AgentTaskStepStatus,
} from "@afar-research/client-runtime/state/agent-task-list";
import type { ScopedThreadRef } from "@afar-research/contracts";
import { CheckIcon, CircleIcon, ListTodoIcon, LoaderCircleIcon } from "lucide-react";
import { memo } from "react";

import { formatDuration } from "~/durationFormat";
import { cn } from "~/lib/utils";

import { Button } from "./ui/button";
import { selectActiveRightPanelSurface, useRightPanelStore } from "../rightPanelStore";

function statusLabel(status: AgentTaskStepStatus): string {
  switch (status) {
    case "completed":
      return "Completed";
    case "inProgress":
      return "In progress";
    case "pending":
      return "Pending";
  }
}

function TaskStatusIcon({ status }: { status: AgentTaskStepStatus }) {
  if (status === "completed") {
    return (
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-success/10 text-success-foreground">
        <CheckIcon aria-hidden className="size-3" />
      </span>
    );
  }
  if (status === "inProgress") {
    return (
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <LoaderCircleIcon aria-hidden className="size-3 animate-spin motion-reduce:animate-none" />
      </span>
    );
  }
  return (
    <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground/45">
      <CircleIcon aria-hidden className="size-3" />
    </span>
  );
}

function currentStepLabel(taskList: AgentTaskListState): string {
  if (taskList.currentStepIndex === null) return "All tasks completed";
  return taskList.steps[taskList.currentStepIndex]?.step ?? "Tasks in progress";
}

export const AgentTaskListControl = memo(function AgentTaskListControl({
  taskList,
  threadRef,
}: {
  taskList: AgentTaskListState | null;
  threadRef: ScopedThreadRef;
}) {
  const active = useRightPanelStore((state) => {
    const surface = selectActiveRightPanelSurface(state.byThreadKey, threadRef);
    return (
      surface?.kind === "tasks" && taskList !== null && surface.resourceId === taskList.instanceId
    );
  });
  if (!taskList) return null;
  const stepLabel = currentStepLabel(taskList);

  return (
    <Button
      type="button"
      size="xs"
      variant={active ? "secondary" : "outline"}
      aria-label={`Open Tasks: ${stepLabel}, ${taskList.completedCount} of ${taskList.totalCount} completed`}
      data-agent-task-list-control="true"
      data-active={active ? "true" : "false"}
      className="max-w-48 gap-1.5 [-webkit-app-region:no-drag]"
      onClick={() => useRightPanelStore.getState().openTasks(threadRef, taskList.instanceId)}
    >
      <ListTodoIcon aria-hidden className="size-3.5 shrink-0" />
      <span>Tasks</span>
      <span aria-live="polite" className="tabular-nums text-muted-foreground">
        {taskList.completedCount}/{taskList.totalCount}
      </span>
    </Button>
  );
});

export const AgentTaskListPanel = memo(function AgentTaskListPanel({
  taskList,
}: {
  taskList: AgentTaskListState;
}) {
  return (
    <section aria-label="Agent task list" className="flex min-h-0 flex-1 flex-col">
      <header className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-foreground">Tasks</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Provider-reported progress for this prompt
            </p>
          </div>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {taskList.completedCount} of {taskList.totalCount} completed
          </span>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {taskList.explanation ? (
          <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
            {taskList.explanation}
          </p>
        ) : null}
        <ol className="space-y-1">
          {taskList.steps.map((task, index) => {
            const isActive = task.status === "inProgress";
            return (
              <li
                key={task.id}
                aria-current={isActive ? "step" : undefined}
                className={cn(
                  "flex items-start gap-2.5 rounded-lg px-2.5 py-2",
                  isActive && "bg-blue-500/8",
                  task.status === "completed" && "bg-emerald-500/5",
                )}
              >
                <TaskStatusIcon status={task.status} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={cn(
                        "text-[13px] leading-snug",
                        task.status === "completed"
                          ? "text-muted-foreground/60 line-through decoration-muted-foreground/25"
                          : isActive
                            ? "font-medium text-foreground"
                            : "text-muted-foreground",
                      )}
                    >
                      {task.step}
                    </p>
                    <span className="shrink-0 text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
                      {isActive ? "Current · In progress" : statusLabel(task.status)}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground/70">
                    <span aria-hidden>{index + 1}</span>
                    {task.durationMs !== undefined ? (
                      <span aria-label={`Duration ${formatDuration(task.durationMs)}`}>
                        {formatDuration(task.durationMs)}
                      </span>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
});
